import React, { useCallback, useEffect, useState } from 'react';
import { Download, IndianRupee, Info, Loader2, TriangleAlert } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CollarSelect, SectionSelect } from '@/components/staffAttendance/SectionSelect';
import { CollarTiles } from '@/components/staffAttendance/CollarTiles';
import { PayrollAgencyCard, money } from '@/components/staffAttendance/PayrollAgencyCard';
import { PayrollReviewList } from '@/components/staffAttendance/PayrollReviewList';
import { AgencyRatesDialog } from '@/components/staffAttendance/AgencyRatesDialog';
import { matchStaff, SearchNote } from '@/components/staffAttendance/StaffSearch';
import { staffAttApi, errMsg, todayIST } from '@/services/staffAttendanceApi';
import { downloadCsv } from '@/services/transportApi';

const ALL = '';
const STATS = [['workers', 'Workers'], ['days', 'Days'], ['truck_days', 'Truck Loadman days'], ['unload_hours', 'Unloading hrs'],
  ['piece_days', 'Piece Rate days'], ['hours', 'Hours worked'], ['review', 'HR to decide'], ['missed', 'Missed check-outs'], ['pending', 'Waiting approval']];
const WARN = new Set(['missed', 'review']);
const MAX_DAYS = 62;
const monthStart = () => `${todayIST().slice(0, 8)}01`;
const span = (a, b) => Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 864e5) + 1;
const rangeError = (a, b) => (!a || !b ? 'Pick both From and To dates'
  : a > b ? 'From date must be on or before To date'
    : span(a, b) > MAX_DAYS ? `Pick a range of ${MAX_DAYS} days or less (this one is ${span(a, b)} days)` : '');

const Totals = ({ t }) => (
  <div className="grid grid-cols-2 md:grid-cols-5 xl:grid-cols-10 gap-3" data-testid="payroll-totals">
    {STATS.map(([k, l]) => (
      <div key={k} className="rounded-xl bg-white shadow-sm p-3">
        <div className="text-[11px] uppercase tracking-wide text-gray-500">{l}</div>
        <div className={`text-2xl font-bold tabular-nums ${WARN.has(k) && t[k] ? 'text-amber-600' : ''}`} data-testid={`payroll-total-${k}`}>{t[k]}</div>
      </div>
    ))}
    <div className="rounded-xl bg-[#1A1F2E] text-white shadow-sm p-3">
      <div className="text-[11px] uppercase tracking-wide text-white/60">Bill amount</div>
      <div className="text-2xl font-bold tabular-nums" data-testid="payroll-total-amount">{money(t.amount)}</div>
    </div>
  </div>
);

/** Contract workers' pay for a date range under the contract pay rules, grouped by agency (CSV + PDF per agency). */
export const ContractPayroll = ({ sections, search = '', canEdit = false }) => {
  const [f, setF] = useState({ date_from: monthStart(), date_to: todayIST(), agency: ALL, section: '', collar: '' });
  const [data, setData] = useState(null);
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ratesOpen, setRatesOpen] = useState(false);
  const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
  const err = rangeError(f.date_from, f.date_to);
  const load = useCallback(() => {
    if (err) return;
    setLoading(true);
    staffAttApi.contractPayroll(params).then(setData).catch((e) => toast.error(errMsg(e, 'Failed to load payroll'))).finally(() => setLoading(false));
  }, [JSON.stringify(params)]); // eslint-disable-line react-hooks/exhaustive-deps
  const loadAgencies = () => staffAttApi.contractAgencies().then((d) => setAgencies(d.agencies)).catch(() => {});
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadAgencies(); }, []);
  const csv = () => downloadCsv(`/staff-attendance/contract-payroll?${new URLSearchParams({ ...params, format: 'csv' })}`, `contract_payroll_${f.date_from}_to_${f.date_to}.csv`).catch((e) => toast.error(errMsg(e, 'Export failed')));
  const t = data?.totals;
  const searching = !!search.trim();
  const shown = (data?.agencies || []).map((a) => (searching ? { ...a, staff: a.staff.filter((s) => matchStaff(search, s.staff_name, s.staff_id)) } : a))
    .filter((a) => !searching || a.staff.length);
  const reviews = (data?.reviews || []).filter((r) => !searching || matchStaff(search, r.staff_name, r.staff_id));
  const count = (list) => list.reduce((n, a) => n + a.staff.length, 0);
  return (
    <div className="space-y-4" data-testid="contract-payroll">
      <div className="flex flex-wrap items-center gap-2">
        <Input type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} className="w-[160px] bg-white" data-testid="payroll-from" />
        <span className="text-sm text-gray-500">to</span>
        <Input type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} className="w-[160px] bg-white" data-testid="payroll-to" />
        <select value={f.agency} onChange={(e) => setF({ ...f, agency: e.target.value })} className="h-10 rounded-md border border-gray-300 bg-white px-3 text-sm" data-testid="payroll-agency-filter">
          <option value={ALL}>All agencies</option>
          {agencies.map((a) => <option key={a.name} value={a.name}>{a.name}</option>)}
          <option value="No agency">No agency</option>
        </select>
        <SectionSelect sections={sections} value={f.section} onChange={(v) => setF({ ...f, section: v })} testId="payroll-section" />
        <CollarSelect value={f.collar} onChange={(v) => setF({ ...f, collar: v })} testId="payroll-collar-select" />
        <Button variant="outline" onClick={csv} disabled={!!err} data-testid="payroll-csv-btn"><Download className="w-4 h-4 mr-1" />Download CSV</Button>
        {canEdit && <Button variant="outline" onClick={() => setRatesOpen(true)} data-testid="payroll-rates-btn"><IndianRupee className="w-4 h-4 mr-1" />Agency rates</Button>}
        {loading && <Loader2 className="w-4 h-4 animate-spin text-amber-600" />}
      </div>
      {err ? <p className="rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-sm text-rose-700" data-testid="payroll-range-error">{err}</p> : <>
      {data && (
        <p className="text-sm text-gray-600" data-testid="payroll-period">
          Showing <b>{data.period}</b> · {data.days.length} day{data.days.length === 1 ? '' : 's'}
        </p>
      )}
      {data?.rules && (
        <div className="flex items-start gap-2 rounded-lg bg-white border border-gray-200 px-3 py-2 text-xs text-gray-600" data-testid="payroll-rules">
          <Info className="w-4 h-4 mt-0.5 text-[#2D5F4F] shrink-0" />{data.rules}
        </div>
      )}
      {t?.collar_split && <CollarTiles split={t.collar_split} value={f.collar} onPick={(v) => setF({ ...f, collar: v })} metric="Bill amount"
        fmt={money} sub={(s) => `${s.staff} worker${s.staff === 1 ? '' : 's'}`} testId="payroll-collar-tiles" />}
      {t && <Totals t={t} />}
      {t?.pending > 0 && (
        <div className="flex items-center gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800" data-testid="payroll-pending-note">
          <TriangleAlert className="w-4 h-4" />{t.pending} check-in / check-out(s) still waiting approval — those shifts aren't paid until approved.
        </div>
      )}
      {t?.unrated?.length > 0 && <p className="text-xs text-gray-500" data-testid="payroll-unrated-note">Rates missing: {t.unrated.join(' · ')} — set them in Agency rates to see the full bill amount.</p>}
      <PayrollReviewList reviews={reviews} canDecide={canEdit && !!data?.can_decide} onChanged={load} />
      {data && data.agencies.length > 0 && <SearchNote q={search} shown={count(shown)} total={count(data.agencies)} testId="payroll-search-note" />}
      {!data ? <Loader2 className="w-6 h-6 animate-spin mx-auto my-8 text-amber-600" /> : data.agencies.length === 0 ? (
        <div className="rounded-xl bg-white shadow-sm py-12 text-center text-gray-500" data-testid="payroll-empty">No contract worker attendance in this date range</div>
      ) : shown.map((a) => <PayrollAgencyCard key={a.agency} a={a} dateFrom={data.date_from} dateTo={data.date_to} section={f.section} expand={searching} />)}
      </>}
      {canEdit && <AgencyRatesDialog open={ratesOpen} onClose={() => setRatesOpen(false)} onSaved={() => { setRatesOpen(false); loadAgencies(); load(); }} />}
    </div>
  );
};

export default ContractPayroll;
